import React, { useEffect, useState } from 'react';
import {
  Box,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Theme,
  Typography,
  withStyles,
} from '@material-ui/core';
import EditIcon from '../../../../utils/svgs/EditIcon';
import { Transition } from '../../../confirmationDialog/ConfirmationDialog';
import colors from '../../../../utils/colors';
import classNames from 'classnames';
import detailsPageStyles from '../../../commonStyles/detailsPage.style';
import selectStyles from '../../../select/select.styles';
import { processSearchQuery } from '../searchQuery';
import Highlighter from 'react-highlight-words';
import configPageStyles from '../../../commonStyles/configPage.style';
import { CurrentView, ModuleName } from '../Modules';
import { INTERACTION } from '../../utils';

// Redux
import { useSearchResultSections } from '../../useSearchResultSections';
import { CSButton } from '../../../primitives';

interface Props {
  classes: { [key: string]: string };
  data: any;
  interaction?: INTERACTION;
  setPartialData: (label_definition: any) => void;
  currentView: CurrentView;
}

const keyToLabel = (key: string): string => {
  const keyParts = key.split('_');
  return `Size: ${keyParts[1]} DPI: ${keyParts[3]}`;
};

const PrintedLabel: React.FC<Props> = ({
  classes,
  data,
  interaction,
  setPartialData,
  currentView,
}) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [selectedFormat, setSelectedFormat] = useState<string>(
    Object.keys(data)[0] || 'format_4x8_dpi_300',
  );
  const [searchResult, setSearchResult] = useState<string[]>([]);

  const SECTION_TITLE = 'Printed Label';

  const { searchQuery, updateSections } = useSearchResultSections(
    ModuleName.OPERATOR_TOOL,
    SECTION_TITLE,
  );

  useEffect(() => {
    if (currentView === CurrentView.SEARCH) {
      const results = processSearchQuery(searchQuery, [
        SECTION_TITLE,
        keyToLabel(selectedFormat),
      ]);

      setSearchResult(results);
      updateSections(results);
    } else {
      setSearchResult([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentView]);

  const onAfterDialogClose = () => {
    setShowDialog(false);
  };

  const getAndSetActiveFormat = (): void => {
    for (let format in data) {
      if (data[format]) {
        setSelectedFormat(format);
      }
    }
  };

  useEffect(getAndSetActiveFormat, [data]);

  const renderDialog = () => {
    return (
      <Dialog
        classes={{ paperScrollPaper: classes.dialogRoot }}
        open={showDialog}
        TransitionComponent={Transition}
        onClose={() => {
          onAfterDialogClose();
        }}
      >
        <DialogTitle>
          <Typography>Edit Printed Label</Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogRoot}>
          <FormControl
            className={classes.formControl}
            style={{
              width: '100%',
              margin: '10px 0px 10px 0',
              height: 50,
              borderColor: colors.black,
            }}
          >
            <InputLabel className={classes.selectLabel}>
              Size and DPI
            </InputLabel>
            <Select
              data-testid='printed-label-dialog-selected-label'
              disableUnderline={true}
              displayEmpty
              classes={{
                selectMenu: classes.selectMenu,
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
                classes: {
                  paper: classes.selectPaper,
                  list: classes.selectList,
                },
              }}
              value={selectedFormat}
              onChange={(e) => {
                const value = e.target.value as string;
                setSelectedFormat(value);
              }}
            >
              <MenuItem value={'Please select...'} disabled>
                Please select...
              </MenuItem>
              {data &&
                Object.keys(data).map((format: string) => (
                  <MenuItem
                    value={format}
                    key={format}
                    data-testid={`printed-label-dialog-label-${format}`}
                  >
                    {keyToLabel(format)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <CSButton
            variant='outlined'
            color='secondary'
            onClick={() => {
              getAndSetActiveFormat(); //Reset to currently active value.
              onAfterDialogClose();
            }}
          >
            Cancel
          </CSButton>
          <CSButton
            variant='contained'
            color='secondary'
            data-testid='printed-label-dialog-save-btn'
            onClick={() => {
              let newData = { ...data };
              for (let format in newData) {
                newData[format] = false; //Reset old values
              }
              newData[selectedFormat] = true;
              setPartialData({
                ...newData,
              });
              onAfterDialogClose();
            }}
          >
            Update
          </CSButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderComponent = () => {
    return (
      <>
        {renderDialog()}
        <Grid item sm={12}>
          <Box>
            <Box
              className={classNames(
                classes.moduleTitle,
                classes.pLeft,
              )}
              mb={1}
              mt={2}
            >
              <Highlighter
                highlightClassName={classes.searchResultsMark}
                searchWords={searchResult}
                autoEscape={true}
                textToHighlight={SECTION_TITLE}
              />
            </Box>
            <Box mt={2}>
              <Grid
                container
                item
                sm={12}
                key={selectedFormat}
                className={classes.row}
              >
                <Grid
                  item
                  sm={11}
                  xs={10}
                  className={classNames(classes.text, classes.pLeft)}
                >
                  <Highlighter
                    highlightClassName={classes.searchResultsMark}
                    searchWords={searchResult}
                    autoEscape={true}
                    textToHighlight={keyToLabel(selectedFormat)}
                  />
                </Grid>
                <Grid item sm={1} xs={2}>
                  <IconButton
                    data-testid='printed-label-edit-btn'
                    size='small'
                    disabled={interaction === INTERACTION.READ}
                    className={classes.editIcon}
                    onClick={() => {
                      setShowDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </>
    );
  };

  return currentView === CurrentView.SEARCH && !searchResult.length
    ? null
    : renderComponent();
};

export default withStyles(
  createStyles((theme: Theme) => ({
    ...detailsPageStyles,
    ...selectStyles,
    ...configPageStyles(theme),
  })),
)(PrintedLabel);
